import { useEffect, useState } from "react"
import { useGetDucInfoByIDQuery, useGetDUCInfoQuery, useGetOwnershipTransferPrefillQuery } from "../../api/ducApiSlice";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { useUserInfoQuery } from "../../api/userApiSlice";
import { colours } from "../../StyleConstants";
import useGetStepHookDucProcess from "./UseGetStepHookDucProcess";
import { useNavigate } from "react-router-dom";
import {
    RenderCurrentDucStep
} from "./components/DucFlows";

export enum DUCProcessSteps {
    step1 = 1,
    step2 = 2,
    step3 = 3,
    step4 = 4,
    step5 = 5,
    step6 = 6,
    step7 = 7
}

export enum steps {
    RENEWAL = 'Renewal',
    REVIEW = 'Review',
    AUTHORIZEDOFFICIAL = 'AuthorizedOfficial',
    SUBRECIPIENTS = 'SubRecipients',
    ATTESTATION = 'Attestation',
    STATEMENT = 'Statement',
    RECIPIENTS = 'Recipients',
}

const DUCProcess = () => {
    const { data: userData } = useUserInfoQuery({});
    const [curStep, setCurStep] = useState(DUCProcessSteps.step1);
    const [DUCInfo, setDUCInfo] = useState({});
    const [dataForHook, setDataForHook] = useState({})
    const [expiryCheck, setExpiryCheck] = useState(false);
    const [fromStatement, setFromStatement] = useState(false);
    //Both of these local storage items get set in the PI modal
    const formType = localStorage.getItem('formType') || ""
    const isGroup = localStorage.getItem('isGroup') === "true"
    const skipPrevData = localStorage.getItem('skipPrevData') === "true"
    //This gets set in the general and aian block component respectively
    const isEdit = localStorage.getItem('edit') === "true"
    const isAddSub = localStorage.getItem('AddSub') === "true"
    const isGroupFlow = isGroup || isAddSub
    const oldStatus = localStorage.getItem('status')
    // Set by IncomingOwnershipTransferBanner when a new PI accepts a transfer (LC-221). Only ever
    // meaningful for the fresh/new-group-DUC path it triggers (edit=false, AddSub=false,
    // status=NotSubmitted) - gating on those same flags means a stale leftover value in
    // localStorage can never attach itself to an unrelated edit/renewal/add-sub/other-DUC-type flow.
    const rawOwnershipTransferId = localStorage.getItem('ownershipTransferId')
    const isAcceptingTransfer = !!rawOwnershipTransferId && !isEdit && !isAddSub && oldStatus === "NotSubmitted"
    const ownershipTransferId = isAcceptingTransfer ? rawOwnershipTransferId : null
    const { data: transferPrefill } = useGetOwnershipTransferPrefillQuery(
        { transferId: ownershipTransferId },
        { skip: !ownershipTransferId }
    )
    //This is and setID is needed to call the useGetDucInfoByIDQuery it
    //relies on the ducID which is received from the useGetDUCInfoQuery call
    const [id, setID] = useState(0)
    const [prevData, setPrevData] = useState({})
    const [isSub, setIsSub] = useState(false)
    const [isSecond, setIsSecond] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [stepLabels, setStepLabels] = useState([]);
    const [loadingPrevData, setloadingPrevData] = useState(true)
    const [skip, setSkip] = useState(true)
    const navigate = useNavigate();
    const { data: DUCData, refetch, isLoading } = useGetDUCInfoQuery({ email: userData['email'] })
    const { data: dataByID, } = useGetDucInfoByIDQuery({ id: id }, { skip: skip })
    // @ts-ignore
    const attestationEnabled = window.env.attestationEnabled;


    useEffect(() => {
        // Prevent browser from restoring the page from back/forward cache (bfcache)
        // When a user navigates away and returns using the browser's Back/Forward buttons,
        // some browsers (like Chrome and Safari) may restore the page from memory (bfcache)
        // instead of re-running useEffect or fetching fresh data.
        // This check reloads the page if it was loaded from bfcache to ensure state is clean.
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                // Force reload if page is restored from bfcache
                window.location.reload();
            }
        };

        window.addEventListener("pageshow", handlePageShow);
        return () => {
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, []);


    //This refetches the useGetDucInfoByIDQuery because on first call it will fail since we do not
    //have an id yet and are still waiting for from useGetDucInfoByIDQuery once it gets set we can
    //refetch and will receive the correct information
    useEffect(() => {
        if(id !== 0 ){
            setSkip(false)
        }

    }, [id]);


    useEffect(() => {
        // When skipPrevData is set (e.g., sub-applicant with DucExpired starting a renewal),
        // avoid fetching or using previous DUC details to ensure a clean, fresh flow.
        if (skipPrevData) {
            setPrevData({})
            setloadingPrevData(false)
            return
        }
        // Accepting an ownership transfer (LC-221): seed the form with the original DUC's content
        // instead of starting blank, using the exact same prevData.data.* shape every step already
        // reads (statement/subApplicants/businessName/businessEmail etc.) - no step components need
        // to change. Wait for the prefill to load before flipping loadingPrevData off, so the wizard
        // doesn't briefly render with empty fields.
        if (ownershipTransferId) {
            if (transferPrefill) {
                setPrevData({
                    data: {
                        firstName: transferPrefill.newPiFirstName,
                        lastName: transferPrefill.newPiLastName,
                        email: transferPrefill.newPiEmail,
                        institution: transferPrefill.newPiInstitution,
                        businessName: transferPrefill.initialSoName,
                        businessEmail: transferPrefill.initialSoEmail,
                        statement: transferPrefill.statement,
                        doi: transferPrefill.doi,
                        pubMed: transferPrefill.pubMed,
                        progressReportStatement: transferPrefill.progressReportStatement,
                        description: transferPrefill.description,
                        subApplicants: transferPrefill.subApplicants,
                    }
                })
                setloadingPrevData(false)
            }
            return
        }
        if (!isEdit && oldStatus === "NotSubmitted") {
            setPrevData({})
            setloadingPrevData(false)
        } else if (dataByID) {
            setPrevData(dataByID)
            setloadingPrevData(false)
        }

    }, [dataByID, skipPrevData, ownershipTransferId, transferPrefill]);

    // Carry the transfer id through to the final submitDUC payload (DucRequest.ownershipTransferId
    // on the backend) - each step's setDUCInfo merges {...DUCInfo, ...newData}, so once set here it
    // survives every later step untouched, since no step's own fields ever use this key.
    useEffect(() => {
        if (ownershipTransferId) {
            setDUCInfo((prev: any) => ({ ...prev, ownershipTransferId: Number(ownershipTransferId) }))
        }
    }, [ownershipTransferId]);

    useEffect(() => {
        if(userData?.['email']){
            refetch()
        }
    }, [userData]);


    useEffect(() => {
        if(oldStatus === null){
            navigate("/duc")
        }
    }, [oldStatus]);


    useEffect(() => {
        if (DUCData) {
            // A sub-applicant starting their own renewal (skipPrevData flow) is acting as the LEAD
            // of the new DUC, even though the backend still classifies them as a sub-applicant of
            // the old, expired DUC until the new one is submitted. Treating them as a sub here would
            // show them the "Your PI has submitted the form..." banner and disable the signing step
            // while THEY are the PI submitting the renewal.
            setIsSub(!!DUCData?.subApplicantGeneral && !skipPrevData)
            if(DUCData?.[formType]?.[1]){
                if(DUCData?.[formType]?.[1]?.status !== oldStatus && !submitting){
                    navigate("/duc")
                }
                if (DUCData?.[formType]?.[1]?.status === "Declined"){
                    setDataForHook(DUCData?.[formType]?.[0])
                    setID(DUCData?.[formType]?.[0]?.ducDetailsId)
                }
                else {
                    setDataForHook(DUCData?.[formType]?.[1])
                    setID(DUCData?.[formType]?.[1]?.ducDetailsId)
                }
                setIsSecond(true)
            } else {
                if(DUCData?.[formType]?.[0]?.status !== oldStatus && !submitting){
                    navigate("/duc")
                }
                setDataForHook(DUCData?.[formType]?.[0])
                setID(DUCData?.[formType]?.[0]?.ducDetailsId)
            }
            return
        }
    }, [formType, DUCData]);

    // Before computing steps, prefer the DETAILS endpoint's `type` in Edit or View flows so renewal intent isn't lost
    const serverDetailsType = (prevData as any)?.data?.type
    const dataForHookPatched: any = ((isEdit || oldStatus === 'Submitted') && serverDetailsType === 'Renewal')
        ? { ...(dataForHook as any), type: 'Renewal' }
        : dataForHook

    //This hook just determines what step of the duc process we should currently be on
    //depending on status and renewal and if it's a group duc and also if it's being edited and resubmitted
    useGetStepHookDucProcess(dataForHookPatched, setDUCInfo, setCurStep, setExpiryCheck, setStepLabels, expiryCheck, DUCInfo,
        formType, userData['username'], isGroupFlow, isEdit, isAddSub, isSub, curStep, (DUCData as any)?.ducStatus)


    return (
        <Stack flexDirection="column" gap={4} height="100%" marginX="50px">
            <Stack gap={3}>
                <Typography
                    variant="h1"
                    fontWeight="600"
                    fontSize="34px"
                    textAlign="center"
                    color={colours.midnightBlue.blue}>
                    Data Use Certification for NIH Brain Development Cohorts
                </Typography>
                {formType === "AIAN" &&
                    <Typography
                        fontWeight="400"
                        textAlign="center"
                        fontSize="22px">
                        Data from American Indian/Alaska Native Individuals (A-DUC)
                    </Typography>}
            </Stack>
            {(isLoading || loadingPrevData) &&
                <Stack width="100%" height="100%" flexDirection="column" alignItems="center" justifyContent="center">
                    <CircularProgress size={100} sx={{ width: "250px", height: "250px" }}/>
                </Stack>}

            {!isLoading && !loadingPrevData &&
            (() => {
                const isRenewal = (DUCInfo as any)?.type === 'Renewal' || expiryCheck;
                return (
                    <RenderCurrentDucStep
                        curStep={curStep}
                        setCurStep={setCurStep}
                        stepLabels={stepLabels}
                        prevData={prevData}
                        DUCInfo={DUCInfo}
                        setDUCInfo={setDUCInfo}
                        attestation={attestationEnabled}
                        isEdit={isEdit}
                        isSub={isSub}
                        type={formType}
                        data={dataForHook}
                        isGroup={isGroupFlow}
                        refetch={refetch}
                        expiry={expiryCheck}
                        fromStatement={fromStatement}
                        isAddSub={isAddSub}
                        isSecond={isSecond}
                        setSubmitting={setSubmitting}
                        setFromStatement={setFromStatement}
                        step={curStep}
                        isRenewal={isRenewal}
                        username={userData['username']}
                    />
                );
            })()}
        </Stack>
    )
}

export default DUCProcess